import React, { useCallback, useMemo, useState } from 'react';
import { Button, Column, Input, Row, Section, Text } from '@ydistri/ds';
import { styled } from 'styled-components';
import {
  AsoScenarioComputeMode,
  EntityListSourceType,
  EntityListTypeName,
  Operation,
  SortDirection,
} from '@ydistri/api-sdk';
import { useComputeAsoMutation } from '../apiAso';
import { useStableParams } from '../../../routes/useStableParams';
import { CalculationSelect } from '../../../components/global/CalculationSelect/CalculationSelect';
import { CalculationTableParams, PrivateFilterType } from '../../Calculations/calculationsTypes';
import EntityListSelect from '../../../components/global/EntityListSelect';
import { Radio } from 'antd';
import { ASO_SELECTS_ID, AsoComputeEntityMode, CreateAsoParams } from '../asoLib';
import { useAsoScenarioStatus } from '../hooks/useAsoScenarioStatus';
import scrollIntoView from 'scroll-into-view';

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 2rem;
`;

const ContentWrapper = styled(Column)`
  gap: 1rem;
  padding: 1rem 0;
`;

const manualSelectQueryParams: CalculationTableParams = {
  sortings: [
    {
      fieldName: 'Created',
      direction: SortDirection.Desc,
    },
  ],
  includePrivateCalculations: PrivateFilterType.NONE,
  conditions: [
    {
      fieldName: 'Mode',
      operation: Operation.Eq,
      value: 'Manual',
    },
  ],
};

const minLayerTypes = [EntityListTypeName.MinLayer];

const calculationModeOptions = [
  { label: 'New calculation', value: AsoComputeEntityMode.NEW },
  { label: 'Existing calculation', value: AsoComputeEntityMode.EXISTING },
];

const entityListModeOptions = [
  { label: 'New min layer', value: AsoComputeEntityMode.NEW },
  { label: 'Existing min layer', value: AsoComputeEntityMode.EXISTING },
];

const AsoComputation: React.FC = () => {
  const { asoScenarioId } = useStableParams();
  const [computeAso, { isLoading }] = useComputeAsoMutation();
  const { isLocked } = useAsoScenarioStatus();
  const disabled = isLocked || isLoading;

  const [calculationMode, setCalculationMode] = useState<AsoComputeEntityMode>(
    AsoComputeEntityMode.NEW,
  );
  const [selectedCalculationId, setSelectedCalculationId] = useState<number | null>(null);
  const [newCalculationName, setNewCalculationName] = useState(`ASO | Scenario ${asoScenarioId}`);

  const [entityListMode, setEntityListMode] = useState<AsoComputeEntityMode>(
    AsoComputeEntityMode.NEW,
  );
  const [selectedEntityListId, setSelectedEntityListId] = useState<number | null>(null);
  const [newEntityListName, setNewEntityListName] = useState(`ASO | Scenario ${asoScenarioId}`);

  const handleCompute = useCallback(
    (mode: AsoScenarioComputeMode) => {
      if (asoScenarioId && !isLocked) {
        const asoComputeParams: CreateAsoParams = {
          asoScenarioId,
          mode,
        };

        switch (mode) {
          case AsoScenarioComputeMode.Routes:
            if (calculationMode === AsoComputeEntityMode.NEW) {
              asoComputeParams.isNew = true;
              asoComputeParams.title = newCalculationName;
            } else {
              if (!selectedCalculationId) {
                throw new Error('Selected calculation ID is required for existing calculations');
              }
              asoComputeParams.isNew = false;
              asoComputeParams.calculationId = selectedCalculationId;
            }
            break;
          case AsoScenarioComputeMode.Layers:
            if (entityListMode === AsoComputeEntityMode.NEW) {
              asoComputeParams.isNew = true;
              asoComputeParams.title = newEntityListName;
            } else {
              if (!selectedEntityListId) {
                throw new Error('Selected min layer list ID is required for existing calculations');
              }
              asoComputeParams.isNew = false;
              asoComputeParams.entityListId = selectedEntityListId;
            }
            break;
        }

        // Scroll to the top of the page to see status (if we stayed at the bottom, only white overlay would be visible)
        const asoSelects = document.querySelector<HTMLDivElement>(`#${ASO_SELECTS_ID}`);
        if (asoSelects) scrollIntoView(asoSelects);

        computeAso(asoComputeParams);
      }
    },
    [
      asoScenarioId,
      calculationMode,
      computeAso,
      entityListMode,
      newCalculationName,
      newEntityListName,
      selectedCalculationId,
      selectedEntityListId,
      isLocked,
    ],
  );

  const calculationHeaderActions = useMemo(() => {
    return (
      <Radio.Group
        options={calculationModeOptions}
        value={calculationMode}
        // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- we know its AsoComputeEntityMode
        onChange={e => setCalculationMode(e.target.value as AsoComputeEntityMode)}
        optionType="button"
        buttonStyle="solid"
        disabled={disabled}
      />
    );
  }, [calculationMode, disabled]);

  const entityListHeaderActions = useMemo(() => {
    return (
      <Radio.Group
        options={entityListModeOptions}
        value={entityListMode}
        // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- we know its AsoComputeEntityMode
        onChange={e => setEntityListMode(e.target.value as AsoComputeEntityMode)}
        optionType="button"
        buttonStyle="solid"
        disabled={disabled}
      />
    );
  }, [entityListMode, disabled]);

  return (
    <Wrapper>
      <Row $gap="1rem">
        <Section header="Compute as routes" headerActions={calculationHeaderActions} $width="45rem">
          <ContentWrapper>
            {calculationMode === AsoComputeEntityMode.EXISTING && (
              <CalculationSelect
                selectedCalculationId={selectedCalculationId}
                onChange={calc => setSelectedCalculationId(calc?.id ?? null)}
                queryParams={manualSelectQueryParams}
                placeholder="Select manual calculation"
                disabled={disabled}
              />
            )}
            {calculationMode === AsoComputeEntityMode.NEW && (
              <Row $alignItems="center" $flexGrow={1} $gap="1rem" $padding={1}>
                <Text>Title: </Text>
                <Input
                  value={newCalculationName}
                  onChange={e => setNewCalculationName(e.target.value)}
                  placeholder="New calculation name"
                  $addPadding={true}
                  infoFlexGrow={1}
                  disabled={disabled}
                />
              </Row>
            )}
            <Row $justifyContent="center">
              <Button
                loading={isLoading}
                disabled={isLocked || !asoScenarioId}
                onClick={() => handleCompute(AsoScenarioComputeMode.Routes)}
              >
                Compute routes
              </Button>
            </Row>
          </ContentWrapper>
        </Section>

        <Section
          header="Compute as min layers"
          headerActions={entityListHeaderActions}
          $width="45rem"
        >
          <ContentWrapper>
            {entityListMode === AsoComputeEntityMode.EXISTING && (
              <EntityListSelect
                multiselect={false}
                types={minLayerTypes}
                sourceType={EntityListSourceType.Computation}
                value={selectedEntityListId ?? undefined}
                onChange={id => setSelectedEntityListId(id)}
                allowClear
                disabled={disabled}
              />
            )}
            {entityListMode === AsoComputeEntityMode.NEW && (
              <Row $alignItems="center" $gap="1rem" $padding={1}>
                <Text>Title: </Text>
                <Input
                  value={newEntityListName}
                  onChange={e => setNewEntityListName(e.target.value)}
                  placeholder="New min layer name"
                  $addPadding={true}
                  infoFlexGrow={1}
                  disabled={disabled}
                />
              </Row>
            )}
            <Row $justifyContent="center">
              <Button
                loading={isLoading}
                disabled={isLocked || !asoScenarioId}
                onClick={() => handleCompute(AsoScenarioComputeMode.Layers)}
              >
                Compute min layers
              </Button>
            </Row>
          </ContentWrapper>
        </Section>
      </Row>
    </Wrapper>
  );
};

export default AsoComputation;
